import { ChangeDetectionStrategy, Component, HostBinding, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowRightFromBracket,
  faCalendarDays,
  faGear,
  faHouse,
} from '@fortawesome/free-solid-svg-icons';
import { AuthService } from '../../../services/auth.service';
import { FullNamePipe } from '../../pipes/full-name.pipe';
import { InitialsPipe } from '../../pipes/initials.pipe';
import { UserRolePipe } from '../../pipes/user-role.pipe';
import { TranslocoDirective } from '@jsverse/transloco';
import { environment } from '../../../../environments/environment';
import { EnvironmentLevel } from '../../../core/environment/environment-level';
import { EnvironmentLevelPipe } from '../../pipes/environment-level.pipe';
import { ButtonDirective } from '../../directives/button.directive';
import { WebSocketService } from '../../../core/services/websocket.service';

export interface TabItem {
  id: string;
  labelKey: string;
  icon: IconDefinition;
  route?: string;
  disabled?: boolean;
  children?: TabItem[];
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    FaIconComponent,
    FullNamePipe,
    InitialsPipe,
    UserRolePipe,
    EnvironmentLevelPipe,
    ButtonDirective,
    TranslocoDirective,
  ],
  templateUrl: './sidebar.component.html',
})
export class SidebarComponent {
  @HostBinding('class') public readonly hostClass = 'app-sidebar';

  private readonly _authService = inject(AuthService);
  private readonly _websocketService = inject(WebSocketService);

  public readonly profile = toSignal(this._authService.currentProfile$);
  public readonly settingsIcon = faGear;
  public readonly logoutIcon = faArrowRightFromBracket;
  public readonly version = signal<string>(environment.buildVersion);
  public readonly environmentName = signal<EnvironmentLevel>(
    environment.envLevel,
  );

  private static readonly BASE_ITEMS: TabItem[] = [
    {
      id: 'dashboard',
      labelKey: 'nav.dashboard',
      icon: faCalendarDays,
      route: '/dashboard',
    },
  ];

  // Every role gets this entry: a MEMBER lands on the diets-only tab of `/settings/household`.
  private static readonly HOUSEHOLD_ITEMS: TabItem[] = [
    {
      id: 'household',
      labelKey: 'nav.household',
      icon: faHouse,
      route: '/settings/household',
    },
  ];

  public readonly menuItems = computed<TabItem[]>(() => [
    ...SidebarComponent.BASE_ITEMS,
    ...SidebarComponent.HOUSEHOLD_ITEMS,
  ]);

  onLogout(): void {
    this._websocketService.disconnect();
    this._authService.logout();
  }
}
